/**
 * Tờ "Đổi giờ" — cách dời lịch trên ĐIỆN THOẠI (docs/NOT-DOING.md #13: không kéo-thả trên điện
 * thoại — ngón tay to, kéo sai là trùng lịch). Tablet cũng mở được qua tờ chi tiết.
 *
 * Chọn KTV → ngày → giờ trống THẬT của máy chủ (bỏ qua chính lịch đang dời, để dời 15 phút vẫn
 * thấy giờ), rồi MỘT nút chính "Dời sang 14:30 · Ngày mai". Máy chủ vẫn kiểm lại đủ 4 lớp khi dời.
 */
import { Check, Moon, Sun, Sunset, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type DayAppointment,
  gioPhut,
  ngayVn,
  type Slot,
  tenDichVu,
  tenKhach,
  themNgay,
} from './api.ts'
import { nhanNgayNgan } from './ngay.ts'
import { T } from './text/vi.ts'
import { Rong, ThongBao, To, Xuong } from './ui.tsx'

const NHOM = [
  { ten: T.morning, tu: 0, den: 12 * 60, icon: Sun },
  { ten: T.afternoon, tu: 12 * 60, den: 18 * 60, icon: Sunset },
  { ten: T.evening, tu: 18 * 60, den: 24 * 60, icon: Moon },
]

export function ToDoiGio({
  lich,
  api,
  branchId,
  date,
  nhanVien,
  onDong,
  onXong,
}: {
  lich: DayAppointment
  api: Api
  branchId: string
  date: string
  nhanVien: { id: string; ten: string }[]
  onDong: () => void
  onXong: (staffId: string, startMin: number, ngay: string) => Promise<void>
}) {
  const homNay = ngayVn()
  // Mặc định là chính người đang giữ lịch — trừ khi người đó không còn trong danh sách (đang nghỉ): chọn người đầu.
  const [ktv, setKtv] = useState(
    lich.staffId !== null && nhanVien.some((n) => n.id === lich.staffId)
      ? lich.staffId
      : (nhanVien[0]?.id ?? ''),
  )
  const [ngay, setNgay] = useState(date < homNay ? homNay : date)
  const [khe, setKhe] = useState<Slot[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [gio, setGio] = useState<number | null>(null)
  const [dang, setDang] = useState(false)

  useEffect(() => {
    if (ktv === '') return
    let con = true
    setKhe(null)
    setGio(null)
    setLoi(null)
    api
      .slots(branchId, ngay, lich.serviceVariantId, ktv, lich.appointmentId)
      .then((r) => {
        if (con) setKhe(r.slots)
      })
      .catch((e: unknown) => {
        if (!con) return
        setKhe([])
        setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
      })
    return () => {
      con = false
    }
  }, [api, branchId, ngay, ktv, lich.serviceVariantId, lich.appointmentId])

  const nhanNgay = (d: string) =>
    d === homNay ? T.today : d === themNgay(homNay, 1) ? T.tomorrowShort : nhanNgayNgan(d)
  const cacNgay = Array.from({ length: 7 }, (_, i) => themNgay(homNay, i))
  const laGioCu = (s: number) => s === lich.startMin && ngay === date && ktv === lich.staffId

  return (
    <To nhan={T.rescheduleTitle} onDong={onDong}>
      <header className="to-dau">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{T.rescheduleTitle}</h2>
          <p className="chu-phu">
            {tenKhach(lich.customerName)} · {tenDichVu(lich.serviceName)} ·{' '}
            {T.nowAt(gioPhut(lich.startMin))}
          </p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        <div className="muc-to">
          <h3>{T.detailStaff}</h3>
          <div className="hang-chip">
            {nhanVien.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`chip ${n.id === ktv ? 'dang-chon' : ''}`}
                aria-pressed={n.id === ktv}
                onClick={() => setKtv(n.id)}
              >
                {n.id === ktv && <Check size={16} />}
                {n.ten}
              </button>
            ))}
          </div>
        </div>

        <div className="muc-to">
          <h3>{T.bookingFor}</h3>
          <div className="hang-chip cuon">
            {cacNgay.map((d) => (
              <button
                key={d}
                type="button"
                className={`chip ${d === ngay ? 'dang-chon' : ''}`}
                aria-pressed={d === ngay}
                onClick={() => setNgay(d)}
              >
                {nhanNgay(d)}
              </button>
            ))}
          </div>
        </div>

        <div className="muc-to">
          <h3>{T.timeSection}</h3>
          {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
          {khe === null ? (
            <div className="luoi-khe">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Xuong key={i} cao={44} />
              ))}
            </div>
          ) : khe.length === 0 ? (
            <Rong icon={<Moon size={26} />} tieuDe={T.noSlotForStaff} moTa={T.tryOtherDayOrStaff} />
          ) : (
            NHOM.map((n) => {
              const cac = khe.filter((s) => s.startMin >= n.tu && s.startMin < n.den)
              if (cac.length === 0) return null
              return (
                <div key={n.ten} className="nhom-khe">
                  <h4>
                    <n.icon size={16} />
                    {n.ten}
                  </h4>
                  <div className="luoi-khe">
                    {cac.map((s) => (
                      <button
                        key={s.startMin}
                        type="button"
                        className={`khe ${gio === s.startMin ? 'dang-chon' : ''} ${laGioCu(s.startMin) ? 'gio-cu' : ''}`}
                        aria-pressed={gio === s.startMin}
                        disabled={laGioCu(s.startMin)}
                        onClick={() => setGio(s.startMin)}
                      >
                        {gioPhut(s.startMin)}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-lon"
          disabled={gio === null || dang}
          onClick={() => {
            if (gio === null) return
            setDang(true)
            void onXong(ktv, gio, ngay).finally(() => setDang(false))
          }}
        >
          {dang
            ? T.moving
            : gio === null
              ? T.pickTimeFirst
              : T.moveTo(gioPhut(gio), nhanNgay(ngay))}
        </button>
      </footer>
    </To>
  )
}
